import {
	Divider,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { type Control, type UseFormSetValue, useWatch } from "react-hook-form";
import type { ExpenseFormModel } from "web_myplann/Finances/Forms/useExpenseForm";
import { ClientFinancialPlanExpenseTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { formatCurrency } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { MyPlannInput } from "web_myplann/Shared/Components/MyPlannInput";
import { ShowMoreButton } from "web_myplann/Shared/Components/ShowMoreButton";
import { CurrencyCode } from "web_myplann/Shared/constants";
import { moneyFormatter } from "web_myplann/Shared/Utils/formatters";

type Props = {
	type: ClientFinancialPlanExpenseTypeEnum;
	onClick: () => void;
	withBackground: boolean;
	control: Control<ExpenseFormModel>;
	setValue: UseFormSetValue<ExpenseFormModel>;
	expensesRemove: (index: number) => void;
};

export const ExpenseCard = ({
	type,
	onClick,
	withBackground,
	control,
	setValue,
	expensesRemove,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();

	const expenses = useWatch({ control, name: "expenses" });
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

	const expensesForCard = expenses.filter(
		(e) => e.type === ClientFinancialPlanExpenseTypeEnum[type],
	);

	return (
		<Stack
			pt={1.6}
			bgcolor={withBackground ? theme.palette.deprecated.grey[100] : "unset"}
		>
			<Stack px={1.6}>
				<Stack direction="row" justifyContent="space-between">
					<Typography
						variant="deprecatedBody2"
						color={theme.palette.deprecated.grey[700]}
						lineHeight={"24px"}
					>
						{t(Resources.Finances.Expenses.Types[type].title)}
					</Typography>
					<Typography
						variant="deprecatedBody1"
						color={theme.palette.deprecated.grey[500]}
					>
						{moneyFormatter(
							expensesForCard.reduce(
								(acc, expense) => acc + expense.monthlyPayment,
								0,
							),
							t(Resources.Common.CurrencySign),
						)}
					</Typography>
				</Stack>
				<Typography
					variant="deprecatedCaption"
					color={theme.palette.deprecated.grey[500]}
				>
					{t(Resources.Finances.Expenses.Types[type].description)}
				</Typography>
				<Stack pt={1.2} gap={0.8}>
					{expensesForCard.map((expense) => {
						const index = expenses.findIndex(
							(e) => e.name === expense.name && e.type === expense.type,
						);

						const isFocused = focusedIndex === index;
						const displayValue = isFocused
							? expenses[index].monthlyPayment
							: `${formatCurrency(expenses[index].monthlyPayment, CurrencyCode.CZK)} ${expenses[index].sustainableAmount ? `(${t(Resources.Finances.Expenses.minimumValueDescription)} ${formatCurrency(expenses[index].sustainableAmount, CurrencyCode.CZK)})` : ""}`;

						return (
							<MyPlannInput
								key={index}
								label={t(Resources.Finances.Expenses.Names[expense.name])}
								value={displayValue}
								onChange={(e) =>
									setValue(
										`expenses.${index}.monthlyPayment`,
										Number(e.target.value),
									)
								}
								onFocus={() => setFocusedIndex(index)}
								onBlur={() => setFocusedIndex(null)}
								slotProps={{
									input: {
										endAdornment: (
											<Stack direction="row" alignItems="center" gap={0.8}>
												<IconButton onClick={() => expensesRemove(index)}>
													<Trash2
														size={16}
														color={theme.palette.deprecated.error.main}
													/>
												</IconButton>
												<Typography
													color={theme.palette.deprecated.grey[500]}
													variant="deprecatedBody1"
												>
													{t(Resources.Common.CurrencySign)}
												</Typography>
											</Stack>
										),
									},
								}}
							/>
						);
					})}
				</Stack>

				<Stack pt={1.2} pb={1.6}>
					<ShowMoreButton
						label={t(Resources.ExpenseCard.addExpense)}
						onClick={onClick}
					/>
				</Stack>
			</Stack>
			<Divider />
		</Stack>
	);
};
